/**
 * Client-side perspective rectification without OpenCV/WASM.
 * Maps an arbitrary convex quad (TL→TR→BR→BL) in image space to a rectangle via inverse homography and bilinear sampling.
 */

export type Point = { x: number; y: number };

const BG_RGBA = [255, 255, 255, 255] as const;

/**
 * Fractional inset before the user adjusts corners. Pass ``frac = 0`` for a full-frame quad
 * at the image edges (TL→TR→BR→BL).
 */
export function defaultCornersInset(imageWidth: number, imageHeight: number, frac = 0.06): Point[] {
  const mx = imageWidth * frac;
  const my = imageHeight * frac;
  return [
    { x: mx, y: my },
    { x: imageWidth - mx, y: my },
    { x: imageWidth - mx, y: imageHeight - my },
    { x: mx, y: imageHeight - my },
  ];
}

/** Full image bounds — no automatic inset so users are not forced to expand the crop every time. */
export function defaultCornersFullFrame(imageWidth: number, imageHeight: number): Point[] {
  return defaultCornersInset(imageWidth, imageHeight, 0);
}

export function euclideanDist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Approximate output raster size from average opposing edge lengths. */
export function quadOutputDimensions(corners: Point[]): { outW: number; outH: number } {
  const [c0, c1, c2, c3] = corners;
  const wTop = euclideanDist(c0, c1);
  const wBot = euclideanDist(c3, c2);
  const hLeft = euclideanDist(c0, c3);
  const hRight = euclideanDist(c1, c2);
  const outW = Math.max(48, Math.round((wTop + wBot) / 2));
  const outH = Math.max(48, Math.round((hLeft + hRight) / 2));
  return { outW, outH };
}

function cross(o: Point, a: Point, b: Point): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** Ensures TL→TR→BR→BL is convex (consistent winding); rejects bow-tie shapes. */
export function isConvexQuadOrdered(corners: Point[]): boolean {
  if (corners.length !== 4) return false;
  let sign = 0;
  for (let i = 0; i < 4; i += 1) {
    const o = corners[i]!;
    const a = corners[(i + 1) % 4]!;
    const b = corners[(i + 2) % 4]!;
    const cr = cross(o, a, b);
    if (Math.abs(cr) < 1e-8) continue;
    const s = cr > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return sign !== 0;
}

/**
 * Solve 8×8 linear system for homography entries with h22 = 1, mapping srcᵢ → dstᵢ:
 * Xi ≈ (h00 xi + h01 yi + h02) / (h20 xi + h21 yi + 1)
 */
function computeHomographyFromPts(src: Point[], dst: Point[]): Float64Array {
  const M = new Array(8).fill(0).map(() => new Array(9).fill(0));
  for (let i = 0; i < 4; i += 1) {
    const xi = src[i]!.x;
    const yi = src[i]!.y;
    const Xi = dst[i]!.x;
    const Yi = dst[i]!.y;
    M[i * 2]![0] = xi;
    M[i * 2]![1] = yi;
    M[i * 2]![2] = 1;
    M[i * 2]![6] = -xi * Xi;
    M[i * 2]![7] = -yi * Xi;
    M[i * 2]![8] = Xi;
    M[i * 2 + 1]![3] = xi;
    M[i * 2 + 1]![4] = yi;
    M[i * 2 + 1]![5] = 1;
    M[i * 2 + 1]![6] = -xi * Yi;
    M[i * 2 + 1]![7] = -yi * Yi;
    M[i * 2 + 1]![8] = Yi;
  }
  const sol = gaussianEliminate8(M);
  return new Float64Array([sol[0]!, sol[1]!, sol[2]!, sol[3]!, sol[4]!, sol[5]!, sol[6]!, sol[7]!, 1]);
}

/** Flat row-major 3×3 inverse (invertible matrix assumed). */
function invertHomography33(H: Float64Array): Float64Array {
  const a = H[0]!;
  const b = H[1]!;
  const c = H[2]!;
  const d = H[3]!;
  const e = H[4]!;
  const f = H[5]!;
  const g = H[6]!;
  const h = H[7]!;
  const i = H[8]!;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const D = -(b * i - c * h);
  const E = a * i - c * g;
  const F = -(a * h - b * g);
  const G = b * f - c * e;
  const Hp = -(a * f - c * d);
  const I = a * e - b * d;
  let det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-14) det = det >= 0 ? 1e-14 : -1e-14;
  const invDet = 1 / det;
  return new Float64Array([
    A * invDet,
    D * invDet,
    G * invDet,
    B * invDet,
    E * invDet,
    Hp * invDet,
    C * invDet,
    F * invDet,
    I * invDet,
  ]);
}

function gaussianEliminate8(aug: number[][]): Float64Array {
  const n = 8;
  const A = aug.map((row) => row.slice());
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    let maxAbs = Math.abs(A[col]![col]!);
    for (let r = col + 1; r < n; r += 1) {
      const v = Math.abs(A[r]![col]!);
      if (v > maxAbs) {
        maxAbs = v;
        pivot = r;
      }
    }
    if (maxAbs < 1e-14) throw new Error("singular-homography");
    if (pivot !== col) {
      const tmp = A[col];
      A[col] = A[pivot];
      A[pivot] = tmp;
    }
    const piv = A[col]![col]!;
    for (let j = col; j <= n; j += 1) {
      A[col]![j]! /= piv;
    }
    for (let r = 0; r < n; r += 1) {
      if (r === col) continue;
      const f = A[r]![col]!;
      if (Math.abs(f) < 1e-18) continue;
      for (let j = col; j <= n; j += 1) {
        A[r]![j]! -= f * A[col]![j]!;
      }
    }
  }
  const x = new Float64Array(n);
  for (let i = 0; i < n; i += 1) x[i] = A[i]![n]!;
  return x;
}

function mapInverse(invH: Float64Array, xd: number, yd: number): { sx: number; sy: number } | null {
  const w = invH[6]! * xd + invH[7]! * yd + invH[8]!;
  if (Math.abs(w) < 1e-12) return null;
  const sx = (invH[0]! * xd + invH[1]! * yd + invH[2]!) / w;
  const sy = (invH[3]! * xd + invH[4]! * yd + invH[5]!) / w;
  return { sx, sy };
}

function sampleBilinear(
  data: Uint8ClampedArray,
  srcW: number,
  srcH: number,
  sx: number,
  sy: number,
): [number, number, number, number] {
  if (sx < -1e-6 || sy < -1e-6 || sx >= srcW - 1e-6 || sy >= srcH - 1e-6) {
    return [...BG_RGBA] as [number, number, number, number];
  }
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const x1 = Math.min(x0 + 1, srcW - 1);
  const y1 = Math.min(y0 + 1, srcH - 1);
  const dx = sx - x0;
  const dy = sy - y0;
  const idx = (yy: number, xx: number) => (yy * srcW + xx) * 4;
  const w00 = (1 - dx) * (1 - dy);
  const w10 = dx * (1 - dy);
  const w01 = (1 - dx) * dy;
  const w11 = dx * dy;
  const i00 = idx(y0, x0);
  const i10 = idx(y0, x1);
  const i01 = idx(y1, x0);
  const i11 = idx(y1, x1);
  let r = 0;
  let g = 0;
  let b = 0;
  let a = 0;
  for (let k = 0; k < 4; k += 1) {
    const v =
      data[i00 + k]! * w00 +
      data[i10 + k]! * w10 +
      data[i01 + k]! * w01 +
      data[i11 + k]! * w11;
    if (k === 0) r = v;
    else if (k === 1) g = v;
    else if (k === 2) b = v;
    else a = v;
  }
  return [Math.round(r), Math.round(g), Math.round(b), Math.round(a)];
}

async function yieldToBrowser(): Promise<void> {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

export interface WarpImageOptions {
  jpegQuality?: number;
  /** Downscale source before warp for CPU/memory (corners stay in original image space). */
  maxLongEdge?: number;
  /** Yield every N scanlines so the UI stays responsive. */
  chunkRows?: number;
}

const DEFAULT_JPEG_QUALITY = 0.92;
const DEFAULT_MAX_LONG_EDGE = 2400;
const DEFAULT_CHUNK_ROWS = 28;

/**
 * Perspective-warp ``file`` so ``corners`` (TL, TR, BR, BL in **original** pixel space)
 * becomes a flat rectangle; gaps outside source become white.
 */
export async function warpImageFileWithQuad(file: File, corners: Point[], options?: WarpImageOptions): Promise<File> {
  if (!isConvexQuadOrdered(corners)) throw new Error("invalid-quad");

  const bitmap = await createImageBitmap(file);
  try {
    const nw = bitmap.width;
    const nh = bitmap.height;
    const maxLE = options?.maxLongEdge ?? DEFAULT_MAX_LONG_EDGE;
    const scale = Math.min(1, maxLE / Math.max(nw, nh, 1));
    const sw = Math.max(1, Math.round(nw * scale));
    const sh = Math.max(1, Math.round(nh * scale));

    const scaledCorners = corners.map((p) => ({ x: p.x * scale, y: p.y * scale }));

    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = sw;
    srcCanvas.height = sh;
    const sctx = srcCanvas.getContext("2d", { willReadFrequently: true });
    if (!sctx) throw new Error("no-2d-context");
    sctx.drawImage(bitmap, 0, 0, sw, sh);
    const srcImg = sctx.getImageData(0, 0, sw, sh);

    const { outW, outH } = quadOutputDimensions(scaledCorners);
    const dstRect: Point[] = [
      { x: 0, y: 0 },
      { x: outW - 1, y: 0 },
      { x: outW - 1, y: outH - 1 },
      { x: 0, y: outH - 1 },
    ];

    const H = computeHomographyFromPts(scaledCorners, dstRect);
    const invH = invertHomography33(H);

    const outCanvas = document.createElement("canvas");
    outCanvas.width = outW;
    outCanvas.height = outH;
    const octx = outCanvas.getContext("2d");
    if (!octx) throw new Error("no-2d-context-out");
    const outImg = octx.createImageData(outW, outH);
    const od = outImg.data;
    const sd = srcImg.data;

    const chunk = options?.chunkRows ?? DEFAULT_CHUNK_ROWS;
    for (let y = 0; y < outH; y += 1) {
      if (chunk > 0 && y > 0 && y % chunk === 0) await yieldToBrowser();
      const row = y * outW * 4;
      for (let x = 0; x < outW; x += 1) {
        const mapped = mapInverse(invH, x, y);
        const off = row + x * 4;
        if (!mapped) {
          od[off] = BG_RGBA[0];
          od[off + 1] = BG_RGBA[1];
          od[off + 2] = BG_RGBA[2];
          od[off + 3] = BG_RGBA[3];
          continue;
        }
        const [r, g, b, a] = sampleBilinear(sd, sw, sh, mapped.sx, mapped.sy);
        od[off] = r;
        od[off + 1] = g;
        od[off + 2] = b;
        od[off + 3] = a;
      }
    }

    octx.putImageData(outImg, 0, 0);

    const q = options?.jpegQuality ?? DEFAULT_JPEG_QUALITY;
    const blob: Blob | null = await new Promise((resolve) =>
      outCanvas.toBlob((b) => resolve(b), "image/jpeg", q),
    );
    if (!blob) throw new Error("toBlob-null");

    const stem = (file.name || "scan").replace(/\.[^/.]+$/, "") || "scan";
    return new File([blob], `${stem}-flat.jpg`, { type: "image/jpeg" });
  } finally {
    bitmap.close();
  }
}
